import React, { ReactElement, useEffect, useState } from "react";
import {
    Ticket as ApiTicket,
    Chat, TicketInput, TicketPriority,
    useExecuteTicketActionMutation,
    useSaveTicketMutation,
} from "../../__generated__/api";
import Confirm from "../confirm/Confirm";
import Form, { FormDisplayUserType } from "../form/Form";

export interface Props {
    onHide: () => void;
    actionId: string;
    chat?: Partial<Chat>;
    chatId?: string;
    children?: ReactElement | string;
    onEmptyMessage?: string;
    shouldSaveForm?: boolean;
    onExecuted?: (input: TicketInput) => void
    buttonText?: string;
    ticketInput?: TicketInput;
}

export default function ActionExecute({
    actionId, chat: ticket, chatId, onHide, children, onEmptyMessage, shouldSaveForm, onExecuted, buttonText, ticketInput,
}: Props) {
    const [executeTicketAction] = useExecuteTicketActionMutation();
    const [ticketActionForm, setTicketActionForm] = useState<TicketInput | undefined>(ticketInput);
    const [saveTicket] = useSaveTicketMutation();

    useEffect(() => {
        if (!ticket) {
            return;
        }
        setTicketActionForm({
            subject: ticket?.ticket?.subject ?? ticket?.subject,
            description: ticket?.ticket?.description,
            priority: ticket?.ticket?.priority ?? ticket?.priority as TicketPriority,
            requestorId: ticket?.ticket?.requestorId ?? ticket?.creatorId,
            tags: ticket?.ticket?.tags,
            customFields: ticket.ticket?.customFields?.map(field => ({
                formFieldId: field.formFieldId,
                type: field.type,
                boolean: field.value?.boolean,
                date: field.value?.date,
                displayValue: field.displayValue,
                number: field.value?.number,
                options: field.value?.options,
                string: field.value?.string,
            })),
        });
    }, [ticket]);

    useEffect(() => {
        setTicketActionForm(ticketInput);
    }, [ticketInput]);

    const setAction = (value: Partial<TicketInput>) => {
        let customFields = ticketActionForm?.customFields;
        if (value.customField) {
            customFields = customFields ?? [];

            customFields.push(value.customField);
            customFields = customFields.dedupe(field => field.formFieldId, (__, last) => last);
        }
        const copy: TicketInput = {
            ...ticketActionForm,
            ...value,
            customFields,
        };
        delete copy.customField;

        setTicketActionForm(copy);
    };

    const startTicketAction = () => {
        (async () => {
            if (shouldSaveForm && ticketActionForm && ticket) {
                saveTicket({
                    variables: {
                        id: ticket.ticket?.id as string,
                        ticket: ticketActionForm,
                    },
                });
            }
            if (chatId) {
                await executeTicketAction({
                    variables: {
                        action: {
                            ...ticketActionForm,
                            formId: actionId,
                        },
                        id: chatId,
                    },
                });
            }

            if (ticketActionForm) {
                onExecuted?.(ticketActionForm);
            }

            onHide();
        })();
    };
    return (
        <Confirm
            shouldShow
            onHide={onHide}
            closeText="Close"
            confirmText={buttonText ?? "Execute"}
            onConfirm={() => {
                startTicketAction();
            }}
        >
            <>
                {children}
                <Form
                    isEditable
                    isEditing
                    isFormField
                    onChange={setAction}
                    selectDefaultRequestType
                    shouldShowDescriptions
                    shouldApplyRequired
                    displayUserType={FormDisplayUserType.AGENT}
                    formId={actionId}
                    shouldValidateAutomation
                    ticket={ticketActionForm as ApiTicket}
                    onEmptyMessage={(
                        <div className="text-light text-center">
                            {onEmptyMessage ?? "This action does not have any fields to fill out."}
                        </div>
                    )}
                />
            </>
        </Confirm>
    );
}

ActionExecute.defaultProps = {
    children: "",
    onEmptyMessage: undefined,
    shouldSaveForm: false,
    onExecuted: undefined,
    chatId: undefined,
    chat: undefined,
    buttonText: undefined,
    ticketInput: undefined,
};
